import cn from "classnames";
import React from "react";
import withCheckHOC from "ui-v2/hocs/withCheckbox";
import { Typography } from "ui-v2/components/Typography";
import { ActionType } from "ui-v2/types/radio";
import s from "./Radio.module.scss";
import { CheckboxSize } from "../../types/checkbox";

interface RadioProps extends React.HTMLAttributes<HTMLInputElement> {
  isChecked: boolean;
  disabled: boolean;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  label?: string;
  description?: string;
  action?: keyof typeof ActionType;
  containerStyle?: React.CSSProperties;
  size?: keyof typeof CheckboxSize;
}

const Radio = (props: RadioProps) => {
  const {
    isChecked,
    disabled,
    id,
    label,
    description,
    onChange,
    action = ActionType.left,
    containerStyle = {},
    size = CheckboxSize.large,
    ...propsToFwd
  } = props;

  return (
    <label htmlFor={id} className={s.root}>
      <input
        type="checkbox"
        checked={isChecked}
        onChange={onChange}
        disabled={disabled}
        id={id}
        {...propsToFwd}
      />
      <div
        className={cn(s.radio_wrapper, {
          [s.reverse]: action === ActionType.right,
        })}
        style={containerStyle}
      >
        <div
          className={cn(s.radio, {
            [s.checked]: isChecked,
            [s.disabled]: disabled,
            [s.radio_checked]: !disabled,
          })}
        >
          <div
            className={cn(s.circle, {
              [s.show_circle]: isChecked,
              [s.disabled_circle]: disabled,
            })}
          />
        </div>
        {(!!label || !!description) && (
          <div className={s.text}>
            {!!label && (
              <Typography
                color={disabled ? "disabled" : "primary"}
                variant={
                  size === CheckboxSize.large
                    ? "label_2_medium"
                    : "label_3_regular"
                }
                label={label}
              />
            )}
            {!!description && (
              <Typography
                color={disabled ? "disabled" : "tertiary"}
                variant="label_4_regular"
                label={description}
              />
            )}
          </div>
        )}
      </div>
    </label>
  );
};

/**
 * Radio
 *
 * Supports all HTML input properties.
 *
 * @param isChecked Required. Boolean value indicating whether the radio button is checked (true) or not (false).
 * @param onChange Required. Change event handler that returns the HTMLInputElement.
 * @param disabled Optional. Boolean value indicating whether the radio button is disabled. Default is false.
 * @param label Optional. Title of the radio button, of type string.
 * @param description Optional. Description of the radio button, of type string.
 * @param action Optional. String value representing the position of the action button. Acceptable values are "Right" or "Left". Default is "Left".
 */

const RadioWithHoc = withCheckHOC(Radio);

export default RadioWithHoc;
